import { RunElement } from '../../shared/run-element.js';
import { skinResolutionMixin } from '../../shared/skin-resolution-mixin.js';
import * as defaultSkin from './skins/divider.default.js';

const VALID_ORIENTATIONS = ['horizontal', 'vertical'];

class RunDivider extends skinResolutionMixin(RunElement) {
  #orientation = 'horizontal';
  #decorative = false;

  // Default skin is always bundled; all others lazy-load via the registry.
  static defaultSkin = defaultSkin;
  static skinLoaders = {
    wireframe: () => import('./skins/divider.wireframe.js'),
    bento: () => import('./skins/divider.bento.js'),
  };

  static get observedAttributes() {
    return ['orientation', 'decorative'];
  }

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.#updateAria();
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'orientation':
        this.orientation = newValue;
        break;
      case 'decorative':
        this.decorative = newValue !== null;
        break;
    }
  }

  get orientation() {
    return this.#orientation;
  }

  set orientation(value) {
    const coerced = String(value).trim();
    const resolved = VALID_ORIENTATIONS.includes(coerced) ? coerced : 'horizontal';
    // Reflect even when the resolved value already matches internal state but
    // the attribute is absent/stale (e.g. an invalid value coerced back to the
    // current orientation) — the attribute must always mirror effective state.
    if (this.#orientation === resolved && this.getAttribute('orientation') === resolved) return;
    this.#orientation = resolved;
    this.setAttribute('orientation', resolved);
    this.#updateAria();
    this.requestUpdate();
  }

  get decorative() {
    return this.#decorative;
  }

  set decorative(value) {
    const coerced = Boolean(value);
    if (this.#decorative === coerced) return;
    this.#decorative = coerced;
    this.toggleAttribute('decorative', coerced);
    this.#updateAria();
    this.requestUpdate();
  }

  // Skin DOM + stylesheet are delegated to the resolved skin module by
  // skinResolutionMixin._render(). updated() only carries post-skin host work.
  updated(changedProperties) {
    super.updated?.(changedProperties);
    this.#updateAria();
  }

  #updateAria() {
    if (this.#decorative) {
      this.setAttribute('role', 'none');
      this.removeAttribute('aria-orientation');
    } else {
      this.setAttribute('role', 'separator');
      this.setAttribute('aria-orientation', this.#orientation);
    }
  }
}

if (!customElements.get('run-divider')) {
  customElements.define('run-divider', RunDivider);
}

export { RunDivider };
export default RunDivider;
